/**
 * @typedef {Object} SpiStep
 * @property {string} mediaSrc
 * @property {string} mediaSrcset
 * @property {string} alt
 * @property {string} heading
 * @property {string} body
 */

/**
 * @typedef {Object} SpiItem
 * @property {string} blockId
 * @property {string} navLabel
 * @property {string} heading
 * @property {string} ctaLabel
 * @property {string} heroSrc
 * @property {string} heroSrcset
 * @property {string} heroAlt
 * @property {string} productHandle
 * @property {SpiStep[]} steps
 */

/**
 * @typedef {Object} SpiPayload
 * @property {string} sectionId
 * @property {SpiItem[]} spiItems
 */

const SPI_STEP_LABEL_CLASS = 'inline-flex cursor-pointer select-none';
const SPI_STEP_INPUT_CLASS = 'peer sr-only';
const SPI_STEP_SPAN_CLASS =
  'box-border w-[28px] h-[28px] shrink-0 rounded-[9999px] flex items-center justify-center  bg-gray-slate text-white-true text-14 font-600 leading-none peer-checked:bg-[var(--spi-step-active-bg)] peer-checked:text-[var(--spi-step-active-fg)] peer-checked:border-[var(--spi-step-active-bg)] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-current';

if (!customElements.get('spi-carousel')) {
  /**
   * SPI carousel web component.
   *
   * Responsibilities:
   * - Parse JSON payload emitted by Liquid (`[data-spi-json]`).
   * - React to SPI item and step radio changes.
   * - Re-render media/content and keep ATC state in sync.
   * - Support Theme Editor block selection events.
   */
  class SpiCarousel extends HTMLElement {
    /** Mount the component after insertion into the DOM. */
    connectedCallback() {
      this._mount();
    }

    /** Remove listeners and teardown component state. */
    disconnectedCallback() {
      if (!this._init) return;
      this._init = false;
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('input', this._onChange);
      if (window.Shopify && window.Shopify.designMode) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
      }
    }

    /**
     * Initialize component state, selectors, and listeners from payload.
     * Safe to call more than once; initialization is idempotent.
     *
     * @returns {void}
     */
    _mount() {
      if (this._init) return;

      this._script = this.parentElement.querySelector('script[data-spi-json]');
      if (!this._script) {
        console.log('[spi-carousel] Missing [data-spi-json]');
        return;
      }

      let raw = this._script.textContent.trim();
      try {
        /** @type {SpiPayload} */
        this._data = JSON.parse(raw);
      } catch (e) {
        console.log('[spi-carousel] Invalid JSON', e);
        return;
      }

      this._spiItems = this._data.spiItems || [];
      if (!this._spiItems.length) return;

      this._init = true;

      this._sectionId = this.dataset.sectionId || '';
      this._spiItemName = 'spi-item-' + this._sectionId;
      this._stepName = 'spi-step-' + this._sectionId;

      this._selectors = {
        heroInner: this.querySelector('[data-spi-hero-inner]'),
        stepMediaWrap: this.querySelector('[data-spi-step-media-wrap]'),
        mainTitle: this.querySelector('[data-spi-main-title]'),
        stepRail: this.querySelector('[data-spi-step-rail]'),
        stepHeading: this.querySelector('[data-spi-step-heading]'),
        stepBody: this.querySelector('[data-spi-step-body]'),
        atc: this.querySelector('[data-spi-atc]'),
      };
      this._quickshopModal = document.querySelector('#QuickshopModal');
      this._quickshopMarkupContainer = this._quickshopModal
        ? this._quickshopModal.querySelector('[js-quickshop-markup-container]')
        : null;

      this._spiItemIdx = 0;
      this._stepIdx = 0;

      this._onChange = this._onChange.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);

      this.addEventListener('change', this._onChange);
      this.addEventListener('input', this._onChange);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this._onBlockSelect);
      }

      let firstSpiItem = this._spiItems[0];
      if (firstSpiItem.steps && firstSpiItem.steps.length) {
        this._rebuildStepRadios(firstSpiItem.steps);
        this._applyStep();
      }
      this._syncAtc(firstSpiItem);
    }

    /**
     * Theme Editor hook: select matching SPI item when a block is selected.
     *
     * @param {CustomEvent} event
     * @returns {void}
     */
    _onBlockSelect(event) {
      let blockId = event.detail && event.detail.blockId;
      if (!blockId || !event.target) return;

      if (this.closest('[id^="shopify-section-"]') !== event.target.closest('[id^="shopify-section-"]')) return;

      let idx = this._spiItems.findIndex((b) => b.blockId === blockId);
      if (idx < 0) return;

      let input = this.querySelector('input[name="' + this._spiItemName + '"][value="' + idx + '"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /**
     * Handle delegated form-control changes for SPI item/step radios.
     *
     * @param {Event} e
     * @returns {void}
     */
    _onChange(e) {
      let target = e.target;

      if (target.matches('[data-spi-item-input]')) {
        this._spiItemIdx = parseInt(target.value, 10) || 0;
        this._stepIdx = 0;
        this._applySpiItem();
      } else if (target.matches('[data-spi-step-input]')) {
        this._stepIdx = parseInt(target.value, 10) || 0;
        this._applyStep();
      }
    }

    /**
     * Apply active SPI item values to DOM and ATC state.
     *
     * @returns {void}
     */
    _applySpiItem() {
      /** @type {SpiItem|undefined} */
      let spiItem = this._spiItems[this._spiItemIdx];
      if (!spiItem) return;

      if (this._selectors.mainTitle) {
        this._selectors.mainTitle.textContent = spiItem.heading || '';
      }

      if (this._selectors.heroInner) {
        this._selectors.heroInner.innerHTML = this._imgHtml(spiItem.heroSrc, spiItem.heroSrcset, spiItem.heroAlt);
      }

      this._rebuildStepRadios(spiItem.steps || []);
      this._applyStep();
      this._syncAtc(spiItem);
    }

    /**
     * Build the step radio controls for the provided SPI steps.
     *
     * @param {SpiStep[]} steps
     * @returns {void}
     */
    _rebuildStepRadios(steps) {
      let rail = this._selectors.stepRail;
      if (!rail) return;

      let frag = document.createDocumentFragment();
      steps.forEach((_, i) => {
        let label = document.createElement('label');
        label.className = SPI_STEP_LABEL_CLASS;

        let input = document.createElement('input');
        input.type = 'radio';
        input.name = this._stepName;
        input.value = String(i);
        input.className = SPI_STEP_INPUT_CLASS;
        input.setAttribute('data-spi-step-input', '');
        if (i === this._stepIdx) input.checked = true;

        let stepSpan = document.createElement('span');
        stepSpan.className = SPI_STEP_SPAN_CLASS;
        stepSpan.textContent = String(i + 1);

        label.appendChild(input);
        label.appendChild(stepSpan);
        frag.appendChild(label);
      });

      rail.innerHTML = '';
      rail.appendChild(frag);
    }

    /**
     * Apply active step content (media, heading, body) to DOM.
     *
     * @returns {void}
     */
    _applyStep() {
      /** @type {SpiItem|undefined} */
      let spiItem = this._spiItems[this._spiItemIdx];
      if (!spiItem || !spiItem.steps.length) return;

      let step = spiItem.steps[this._stepIdx] || spiItem.steps[0];

      if (this._selectors.stepMediaWrap) {
        this._selectors.stepMediaWrap.innerHTML = this._imgHtml(step.mediaSrc, step.mediaSrcset, step.alt);
      }
      if (this._selectors.stepHeading) {
        this._selectors.stepHeading.textContent = step.heading || '';
      }
      if (this._selectors.stepBody) {
        this._selectors.stepBody.innerHTML = step.body || '';
      }
    }

    /**
     * Build image markup string from SPI image fields.
     *
     * @param {string} src
     * @param {string} srcset
     * @param {string} alt
     * @returns {string}
     */
    _imgHtml(src, srcset, alt) {
      if (!src) return '';
      let srcSet = srcset
        ? ' srcset="' + this._sanitize(srcset) + '" sizes="(min-width: 1024px) 50vw, 100vw"'
        : '';
      return (
        '<img class="block w-full h-auto object-cover" src="' +
        this._sanitize(src) +
        '"' +
        srcSet +
        ' alt="' +
        this._sanitize(alt || '') +
        '" loading="lazy" width="1400" height="1400">'
      );
    }

    /**
     * Escape values before injecting into HTML strings.
     *
     * @param {string} s
     * @returns {string}
     */
    _sanitize(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
    }

    /**
     * Sync CTA text and quickshop handle.
     *
     * @param {SpiItem} spiItem
     * @returns {void}
     */
    _syncAtc(spiItem) {
      let btn = this._selectors.atc;
      if (!btn) return;

      btn.textContent = (spiItem && spiItem.ctaLabel) || 'Add to Bag';
      btn.dataset.handle = (spiItem && spiItem.productHandle) || '';
      btn.disabled = false;
      btn.setAttribute('js-open-quickshop-modal', '');
      btn.onclick = this._openQuickshopModal;
    }

    /**
     * Fetch quickshop html for the selected product handle.
     *
     * @param {string} url
     * @returns {Promise<string>}
     */
    _getQuickshopMarkup(url) {
      return fetch(url)
        .then((response) => response.text())
        .then((text) => {
          let parsedHTML = new DOMParser().parseFromString(text, 'text/html');
          let quickshopCopy = parsedHTML.querySelector('[js-quickshop-markup-copy]');
          return quickshopCopy ? quickshopCopy.innerHTML : '';
        });
    }

    /**
     * Open the global quickshop modal from SPI CTA.
     *
     * @param {MouseEvent} evt
     * @returns {Promise<void>}
     */
    _openQuickshopModal = async (evt) => {
      evt.stopPropagation();

      if (!this._quickshopModal || !this._quickshopMarkupContainer) return;

      let target = evt.currentTarget;
      if (!target) return;

      let productHandle = target.dataset.handle;
      if (!productHandle) return;

      this._quickshopMarkupContainer.innerHTML = '';
      let targetUrl = `/products/${productHandle}/?view=quick-shop`;
      let quickshopMarkup = await this._getQuickshopMarkup(targetUrl);
      if (!quickshopMarkup) return;

      this._quickshopMarkupContainer.innerHTML = quickshopMarkup;

      let quickshopLayout =
        this._quickshopModal.querySelector('s-product') || this._quickshopModal.querySelector('product-bundle');
      if (quickshopLayout && typeof quickshopLayout.connectedCallback === 'function') {
        quickshopLayout.connectedCallback(true);
      }
      if (quickshopLayout && quickshopLayout.querySelector('product-options')) {
        let quickShopProductOptions = quickshopLayout.querySelectorAll('product-options');
        for (let productOption of quickShopProductOptions) {
          productOption.connectedCallback();
        }
      }

      this._quickshopModal.open();
    }
  }

  customElements.define('spi-carousel', SpiCarousel);
}
